/**
 * BottomSheet hosting directory sort + optional org facet (no search field).
 * OverlayBackDismiss while open so OS/browser back closes the sheet (not navigate).
 * Browse category / map-offer sheets compose this component.
 */

import { useEffect, type ReactNode } from 'react';
import { useOptionalOverlayBackDismiss } from '../context/OverlayBackDismissContext.js';
import { BottomSheet } from './BottomSheet.js';
import { Button } from './Button.js';
import type { DirectoryOrgFacet, DirectorySortId } from './directoryListFilter.js';

export interface DirectoryFilterFacetsProps {
  readonly sort: DirectorySortId;
  readonly onSortChange: (sort: DirectorySortId) => void;
  readonly showOrgFacet: boolean;
  readonly orgFacets: readonly DirectoryOrgFacet[] | undefined;
  readonly orgCode: string | null;
  readonly onOrgCodeChange: ((orgCode: string | null) => void) | undefined;
  /**
   * Sort option ids. Labels come from `directory.filters.sort.${id}`
   * (shop: name/org/distance/favorite; orders: dateAsc/dateDesc/amountAsc/amountDesc).
   */
  readonly sortOptions: readonly DirectorySortId[];
  readonly testIdPrefix: string;
  readonly t: (key: string) => string;
}

/** Sort + org selects — shared by mobile sheet and desktop inline row. */
export function DirectoryFilterFacets(props: DirectoryFilterFacetsProps): JSX.Element {
  const {
    sort,
    onSortChange,
    showOrgFacet,
    orgFacets,
    orgCode,
    onOrgCodeChange,
    sortOptions,
    testIdPrefix,
    t,
  } = props;

  return (
    <div className={`grid gap-2 ${showOrgFacet ? 'sm:grid-cols-2' : 'grid-cols-1'}`}>
      <label className="flex min-w-0 flex-col gap-1 text-xs text-[var(--color-on-surface-muted)]">
        <span>{t('directory.filters.sortLabel')}</span>
        <select
          value={sort}
          onChange={(event) => {
            onSortChange(event.target.value as DirectorySortId);
          }}
          className="min-h-11 w-full rounded-sm border border-[var(--color-border-strong)] bg-[var(--color-surface-elevated)] px-3 py-2 text-sm text-[var(--color-on-surface)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)]"
          data-testid={`${testIdPrefix}-sort`}
        >
          {sortOptions.map((option) => (
            <option key={option} value={option}>
              {t(`directory.filters.sort.${option}`)}
            </option>
          ))}
        </select>
      </label>

      {showOrgFacet && orgFacets !== undefined && onOrgCodeChange !== undefined ? (
        <label className="flex min-w-0 flex-col gap-1 text-xs text-[var(--color-on-surface-muted)]">
          <span>{t('directory.filters.orgLabel')}</span>
          <select
            value={orgCode ?? ''}
            onChange={(event) => {
              const next = event.target.value.trim();
              onOrgCodeChange(next.length > 0 ? next : null);
            }}
            className="min-h-11 w-full rounded-sm border border-[var(--color-border-strong)] bg-[var(--color-surface-elevated)] px-3 py-2 text-sm text-[var(--color-on-surface)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)]"
            data-testid={`${testIdPrefix}-org`}
          >
            <option value="">{t('directory.filters.orgAll')}</option>
            {orgFacets.map((facet) => (
              <option key={facet.code} value={facet.code}>
                {facet.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}
    </div>
  );
}

export interface DirectoryFilterSheetProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly title: string;
  readonly titleId: string;
  readonly closeLabel: string;
  readonly testIdPrefix: string;
  readonly facetsActive: boolean;
  readonly onClearAll: () => void;
  readonly clearAllLabel: string;
  readonly doneLabel: string;
  readonly children: ReactNode;
}

export function DirectoryFilterSheet(props: DirectoryFilterSheetProps): JSX.Element {
  const {
    open,
    onClose,
    title,
    titleId,
    closeLabel,
    testIdPrefix,
    facetsActive,
    onClearAll,
    clearAllLabel,
    doneLabel,
    children,
  } = props;

  const overlayBack = useOptionalOverlayBackDismiss();
  const registerOverlayDismiss = overlayBack?.registerOverlayDismiss;

  useEffect(() => {
    if (!open || registerOverlayDismiss === undefined) {
      return;
    }
    return registerOverlayDismiss(onClose);
  }, [open, onClose, registerOverlayDismiss]);

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={title}
      titleId={titleId}
      closeLabel={closeLabel}
      testId={`${testIdPrefix}-sheet`}
      className="max-h-[90vh] bg-[var(--color-surface-elevated)] p-5 lg:max-w-lg"
      containerClassName="lg:items-center"
    >
      {children}
      <div className="mt-4 flex flex-col gap-2">
        {facetsActive ? (
          <Button
            intent="ghost"
            size="md"
            block
            type="button"
            data-testid={`${testIdPrefix}-clear-all`}
            onClick={onClearAll}
          >
            {clearAllLabel}
          </Button>
        ) : null}
        <Button
          intent="primary"
          size="md"
          block
          type="button"
          data-testid={`${testIdPrefix}-sheet-done`}
          onClick={onClose}
        >
          {doneLabel}
        </Button>
      </div>
    </BottomSheet>
  );
}
